import { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { InputText, InputSelect } from "@2solve/tosolve-inputs";
import CreatableSelect from "react-select/creatable";
import useApi from "../hooks/apiService";
import useResources from "../hooks/useResources";
import useFilteredAttributes from "../hooks/useFilteredAttributes";
import { useToken } from "../hooks/tokenContext";

function ModalResourceAttributeValues({
  show,
  onHide,
  initialResource = "", // Valor inicial para resource
  initialItems = [], // Array de IDs de itens iniciais
  error,
  setError,
  onDataAdded,
  disableLocking = false,
}) {
  const { t } = useTranslation();
  const { fetchData, postData, updateData } = useApi();
  const { permissions } = useToken();
  const hasGlobalAccess = permissions?.register?.attributes === 11;
  const { resources: availableResources, getResourceBySlug } = useResources({
    admin: disableLocking,
  });
  const [selectedResource, setSelectedResource] = useState(initialResource);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(initialItems);
  const [value, setValue] = useState("");
  const [valueOptions, setValueOptions] = useState([]);
  const [existingValues, setExistingValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [noAttributesWarning, setNoAttributesWarning] = useState(false);
  const resource = selectedResource
    ? getResourceBySlug(selectedResource)
    : null;

  const { attributes, loading: attributesLoading } = useFilteredAttributes({
    resourceId: resource?.id,
    disableLocking,
    enabled: show,
  });

  const DATA_TYPE_LABEL = {
    S: t("Texto"),
    N: t("Numérico"),
    B: t("Booleano"),
  };
  useEffect(() => {
    if (show) {
      setSelectedResource(initialResource);
      setSelectedAttribute(null);
      setItems([]);
      setSelectedItems(initialItems);
      setValue("");
      setValueOptions([]);
      setExistingValues({});
      setError("");
    }
  }, [show]);

  const loadItems = async (resource) => {
    try {
      const response = await fetchData(`/${resource}`);
      setItems(response.data || []);
    } catch (err) {
      console.error(err);
      setError(t("Erro ao carregar itens do resource"));
    }
  };

  const handleResourceChange = async (resourceSlug) => {
    setSelectedResource(resourceSlug);
    setSelectedAttribute(null);
    setItems([]);
    setSelectedItems([]);
    setValue("");
    setValueOptions([]);
    setExistingValues({});
    setError("");
    setNoAttributesWarning(false);
  };

  useEffect(() => {
    setNoAttributesWarning(
      !attributesLoading && attributes.length === 0 && !!selectedResource,
    );
  }, [attributes, attributesLoading, selectedResource]);

  const loadValueOptions = async (attributeId) => {
    try {
      const response = await fetchData(
        `/attribute-values/attributes/${attributeId}`,
      );
      const existing = {};
      let data = response.data || response;
      let values = [];
      if (Array.isArray(data)) {
        data.forEach((item) => {
          existing[item.record.id] = { id: item.id, value: item.value };
          values.push(item.value);
        });
      } else if (data && typeof data === "object") {
        Object.values(data).forEach((item) => {
          existing[item.record.id] = { id: item.id, value: item.value };
          values.push(item.value);
        });
      }
      const uniqueSortedValues = [...new Set(values)].sort((a, b) =>
        a.localeCompare(b),
      );
      setValueOptions(
        uniqueSortedValues.map((val) => ({ value: val, label: val })),
      );
      setExistingValues(existing);
      return existing;
    } catch (err) {
      console.error(err);
      setValueOptions([]);
      setExistingValues({});
      return {};
    }
  };

  const handleAttributeChange = async (attributeId) => {
    const attr = attributes.find((a) => a.id === attributeId);

    setSelectedAttribute(attr || null);
    setSelectedItems([]);
    setValue("");
    setError("");

    if (!attr) {
      setItems([]);
      setValueOptions([]);
      setExistingValues({});
      return;
    }

    await loadItems(selectedResource);

    await loadValueOptions(attributeId);
  };

  const closeModal = () => {
    setSelectedResource("");
    setSelectedAttribute(null);
    setItems([]);
    setSelectedItems([]);
    setValue("");
    setValueOptions([]);
    setExistingValues({});
    setError("");
    onHide();
  };

  const getValueInput = () => {
    if (!selectedAttribute) return null;

    switch (selectedAttribute.data_type) {
      case "S":
        return (
          <CreatableSelect
            id="value-input"
            placeholder={t("Digite ou selecione o valor")}
            options={valueOptions}
            value={value ? { value, label: value } : null}
            onChange={(selectedOption) =>
              setValue(selectedOption ? selectedOption.value : "")
            }
            onCreateOption={(inputValue) => setValue(inputValue)}
            isClearable
            formatCreateLabel={(inputValue) => `${t("Criar")} "${inputValue}"`}
          />
        );
      case "N":
        return (
          <InputText
            id="value-input"
            type="number"
            placeholder={t("Digite um valor numérico")}
            value={value}
            onChange={(_, valeu) => {
              const val = valeu;
              if (val && isNaN(val)) {
                setError(t("O valor deve ser numérico"));
                return;
              }
              setValue(val);
              setError("");
            }}
          />
        );
      case "B":
        return (
          <InputSelect
            id="value-input"
            placeholder={t("Selecione um valor")}
            options={[
              { value: "true", label: t("Verdadeiro") },
              { value: "false", label: t("Falso") },
            ]}
            isMultiple={false}
            selectAll={false}
            closeOnSelect={true}
            IsClearable={false}
            onChange={(_, val) => setValue(val)}
            value={value}
          />
        );
      default:
        return (
          <InputText
            id="value-input"
            type="text"
            placeholder={t("Digite o valor")}
            value={value}
            onChange={(_, valeu) => setValue(valeu)}
          />
        );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedResource) {
      setError(t("Selecione um resource"));
      return;
    }
    if (!selectedAttribute) {
      setError(t("Selecione um atributo"));
      return;
    }
    if (selectedItems.length === 0) {
      setError(t("Selecione pelo menos um item"));
      return;
    }
    if (!value) {
      setError(t("Digite um valor"));
      return;
    }
    if (noAttributesWarning) {
      setError(t("O recurso selecionado não possui atributos"));
      return;
    }

    setLoading(true);

    try {
      const freshExistingValues = await loadValueOptions(selectedAttribute.id);

      const updates = [];
      const creates = [];

      selectedItems.forEach((itemId) => {
        if (freshExistingValues[itemId]) {
          updates.push(
            updateData(`/attribute-values/${freshExistingValues[itemId].id}`, {
              value,
            }),
          );
        } else {
          creates.push({
            attribute_id: selectedAttribute.id,
            value: value,
            record_id: itemId,
          });
        }
      });

      // Execute updates
      await Promise.all(updates);

      // Execute creates if any
      if (creates.length > 0) {
        await postData("/attribute-values", creates);
      }

      onDataAdded();
      closeModal();
    } catch (err) {
      console.error(err);
      setError(t("Erro ao salvar valores"));
    } finally {
      setLoading(false);
    }
  };

  const resourceOptions = availableResources
    .filter(
      (res) => hasGlobalAccess || permissions?.register?.[res.slug] === 11,
    )
    .map((res) => ({
      value: res.slug,
      label: res.name,
    }));

  const attributeOptions = attributes.map((attr) => ({
    value: attr.id,
    label: attr.name,
  }));

  const itemOptions = items.map((item) => ({
    value: item.id,
    label:
      item.name ||
      item.slug ||
      item.title ||
      item.email ||
      item.mapping_group?.name + " > " + item.variable?.name ||
      "",
  }));
  const selectedItemOptions = itemOptions.filter((opt) =>
    selectedItems.includes(opt.value),
  );

  return (
    <Modal backdrop="static" size="lg" centered show={show} onHide={closeModal}>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>{t("assign_values_to_attribute")}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="mb-4">
            <label className="form-label">
              <strong>{t("resource")}</strong>
            </label>
            <InputSelect
              id="resource-select"
              placeholder={t("Selecione um recurso")}
              options={resourceOptions}
              isMultiple={false}
              selectAll={false}
              closeOnSelect={true}
              IsClearable={false}
              onChange={(_, val) => handleResourceChange(val)}
              value={selectedResource}
            />
          </div>

          {attributes.length > 0 && (
            <div className="mb-4">
              <label className="form-label">
                <strong>{t("attribute")}</strong>
              </label>
              <InputSelect
                id="attribute-select"
                placeholder={t("Selecione um atributo")}
                options={attributeOptions}
                isMultiple={false}
                selectAll={false}
                closeOnSelect={true}
                IsClearable={false}
                onChange={(_, val) => {
                  handleAttributeChange(val);
                }}
                value={selectedAttribute?.id || ""}
              />
            </div>
          )}

          {selectedAttribute && items.length > 0 && (
            <div className="mb-4">
              <label className="form-label">
                <strong>{t("items")}</strong>
              </label>
              <InputSelect
                id="items-select"
                placeholder={t("Selecione um ou mais itens")}
                options={itemOptions}
                isMultiple={true}
                selectAll={false}
                closeOnSelect={false}
                IsClearable={true}
                onChange={(_, vals) => {
                  setSelectedItems(vals || []);
                }}
                value={selectedItemOptions}
              />
            </div>
          )}

          {selectedAttribute && selectedItems.length > 0 && (
            <div className="mb-4">
              <label className="form-label">
                <strong>{t("value")}</strong> {t("type")}{" "}
                {DATA_TYPE_LABEL[selectedAttribute.data_type]}
                {selectedAttribute.description && (
                  <small className="text-muted">
                    {" "}
                    {t("description")} {selectedAttribute.description}
                  </small>
                )}
              </label>
              {getValueInput()}
            </div>
          )}
          {selectedResource && noAttributesWarning && (
            <div className="alert alert-warning mt-3">
              {t("Este recurso não possui atributos cadastrados")}
            </div>
          )}
          {error && <div className="alert alert-danger mt-3">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            {t("button_cancel")}
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? t("Salvando...") : t("Salvar")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

ModalResourceAttributeValues.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  initialResource: PropTypes.string,
  initialItems: PropTypes.array,
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  onDataAdded: PropTypes.func.isRequired,
};

export default ModalResourceAttributeValues;
